import { Room, RoomType } from 'Interfaces/ReferenceInterfaces/RoomInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { roomScreenServices } from 'services/ReferenceScreenServices/roomScreenServices';

export class RoomStore {
  rooms: Room[] = [];
  roomTypes: RoomType[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  roomTypesLoading: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  roomTypesPagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedRoom: Room | null = null;
  selectedRoomType: RoomType | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  fetchRooms = async (page = 1, pageSize = 10000) => {
    this.isLoading = true;
    try {
      const response = await roomScreenServices.getAllRooms(page, pageSize);
      runInAction(() => {
        this.rooms = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching rooms');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  fetchRoomTypes = async (page = 1, pageSize = 100) => {
    this.roomTypesLoading = true;
    try {
      const response = await roomScreenServices.getAllRoomTypes(page, pageSize);
      runInAction(() => {
        this.roomTypes = response.data;
        this.roomTypesPagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching room types');
    } finally {
      runInAction(() => {
        this.roomTypesLoading = false;
      });
    }
  };

  pollRooms = async (page = 1, pageSize = 10000) => {
    this.isPolling = true;
    try {
      const response = await roomScreenServices.getAllRooms(page, pageSize);
      runInAction(() => {
        if (JSON.stringify(this.rooms) !== JSON.stringify(response.data)) {
          this.rooms = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      if (this.environment === 'development') {
        console.error('Error polling rooms:', error);
      }
      toast.error('Error polling rooms!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  pollRoomTypes = async (page = 1, pageSize = 100) => {
    try {
      const response = await roomScreenServices.getAllRoomTypes(page, pageSize);
      runInAction(() => {
        if (JSON.stringify(this.roomTypes) !== JSON.stringify(response.data)) {
          this.roomTypes = response.data;
          this.roomTypesPagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      if (this.environment === 'development') {
        console.error('Error polling room types:', error);
      }
      toast.error('Error polling room types!');
    }
  };

  createRoom = async (roomData: Omit<Room, 'id' | 'created_at' | 'updated_at'>) => {
    this.isLoading = true;
    try {
      const response = await roomScreenServices.createRoom(roomData);
      if (response.status) {
        toast.success('Room created successfully');
        this.fetchRooms(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to create room');
      }
    } catch (error) {
      toast.error('Error creating room');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createRoomType = async (roomTypeData: Omit<RoomType, 'id'>) => {
    this.roomTypesLoading = true;
    try {
      const response = await roomScreenServices.createRoomType(roomTypeData);
      if (response.status) {
        toast.success('Room type created successfully');
        this.fetchRoomTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to create room type');
      }
    } catch (error) {
      toast.error('Error creating room type');
    } finally {
      runInAction(() => {
        this.roomTypesLoading = false;
      });
    }
  };

  updateRoom = async (
    id: number,
    roomData: Partial<Omit<Room, 'id' | 'created_at' | 'updated_at'>>
  ) => {
    this.isLoading = true;
    try {
      const response = await roomScreenServices.updateRoom(id, roomData);
      if (response.status) {
        toast.success('Room updated successfully');
        this.fetchRooms(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to update room');
      }
    } catch (error) {
      toast.error('Error updating room');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateRoomType = async (id: number, roomTypeData: Partial<Omit<RoomType, 'id'>>) => {
    this.roomTypesLoading = true;
    try {
      const response = await roomScreenServices.updateRoomType(id, roomTypeData);
      if (response.status) {
        toast.success('Room type updated successfully');
        this.fetchRoomTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to update room type');
      }
    } catch (error) {
      toast.error('Error updating room type');
    } finally {
      runInAction(() => {
        this.roomTypesLoading = false;
      });
    }
  };

  deleteRoom = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await roomScreenServices.deleteRoom(id);
      if (response.status) {
        toast.success('Room deleted successfully');
        this.fetchRooms(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete room');
      }
    } catch (error) {
      toast.error('Error deleting room');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteRoomType = async (id: number) => {
    this.roomTypesLoading = true;
    try {
      const response = await roomScreenServices.deleteRoomType(id);
      if (response.status) {
        toast.success('Room type deleted successfully');
        this.fetchRoomTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete room type');
      }
    } catch (error) {
      toast.error('Error deleting room type');
    } finally {
      runInAction(() => {
        this.roomTypesLoading = false;
      });
    }
  };

  setSelectedRoom = (room: Room | null) => {
    this.selectedRoom = room;
  };

  setSelectedRoomType = (roomType: RoomType | null) => {
    this.selectedRoomType = roomType;
  };

  refreshData = async (page = 1, pageSize = 10000) => {
    await this.fetchRooms(page, pageSize);
  };

  refreshRoomTypes = async (page = 1, pageSize = 100) => {
    await this.fetchRoomTypes(page, pageSize);
  };

  // Computed getter for room type options for dropdowns
  get roomTypeOptions() {
    return this.roomTypes.map(roomType => ({
      label: roomType.description || '',
      value: roomType.id
    }));
  }
}

export const roomStore = new RoomStore();
